import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { dialogs, siteLocales, i18next, site } from '@yola/ws-sdk';
import { reactHookForm } from '@yola/ws-ui';
import { constants as subscriptionConstants } from '@yola/subscription-manager-js';
import dialogTypes from 'src/js/modules/dialogs/constants/dialog-types';
import useFeatureFlags from 'yola-editor/src/js/modules/feature-flags/hooks/use-feature-flags';
import tabIdentifiers from 'src/js/modules/website-settings/constants/tab-identifiers';
import validatePageName from 'src/js/modules/page-settings/helpers/validate-page-name';
import config from 'src/js/modules/config';
import ai from '../../ai';
import upsell from '../../upsell';
import AddPageDialog from '../components/add-page-dialog';
import fieldNames from '../constants/field-names';
import getOptionsToCreatePage from '../helpers/get-options-to-create-page';
import pageTypes from '../constants/page-types';
import constants from '../constants';
import getPageNameByType from '../helpers/get-page-name-by-type';
import { generateUniquePageName } from '../helpers/generate-unique-page-meta-value';

const { useForm } = reactHookForm;
const {
  constants: { sourceIds },
  hooks: { useAiOnboarding },
} = ai;
const {
  hostingPackageFeatureNames: { AI_PAGE_WIZARD },
} = subscriptionConstants;

const MAX_USER_INPUT_CHARS = 1000;
const pageTemplatesWithBusinessDescription = [pageTypes.HOME, pageTypes.ABOUT_US];

const getUserInputCaptions = (pageType) => {
  switch (pageType) {
    case pageTypes.HOME:
    case pageTypes.ABOUT_US:
      return { label: i18next.t('Describe your business') };
    case pageTypes.SERVICES:
      return {
        label: `${i18next.t('List your services')}:`,
        statusText: i18next.t('Summarize your services using a few key phrases or keywords'),
      };
    case pageTypes.PRODUCTS:
      return {
        label: `${i18next.t('List your products')}:`,
        statusText: i18next.t(
          'Provide a brief overview of your products, including the product names and prices'
        ),
      };
    case pageTypes.TESTIMONIALS:
      return {
        label: i18next.t('What to emphasize in testimonials?'),
        statusText: i18next.t('Highlight key points to emphasize in the generated testimonials'),
      };
    case pageTypes.MENU:
      return {
        label: `${i18next.t('List your menu')}:`,
        statusText: i18next.t(
          'Give a brief overview of your menu, including item names and prices'
        ),
      };
    case pageTypes.FAQ:
      return {
        label: `${i18next.t('List common FAQs')}:`,
        statusText: i18next.t('List answers to common questions people frequently ask you about'),
      };
    case pageTypes.OUR_WORK:
    case pageTypes.CONTACT_US:
    case pageTypes.EMPTY_PAGE:
    case pageTypes.ALL_BLOCKS:
    default:
      return null;
  }
};

const getCaptions = ({ isEmptyPageSelected, isAllBlocksPageSelected, openWebsiteSettings }) => {
  const defaultCaptions = {
    heading: i18next.t('Add page'),
    description: i18next.t(
      'Choose a page template and let AI generate it for you, or start with a blank page.'
    ),
    button: i18next.t('Generate page'),
    pageTemplateSubtitle: '',
    inputTitle: i18next.t('Page name'),
    checkboxTitle: i18next.t('Display in navigation'),
    previewLabel: i18next.t('Generated'),
  };

  if (isEmptyPageSelected) {
    return {
      ...defaultCaptions,
      button: i18next.t('Create page'),
      pageTemplateSubtitle: i18next.t('Start from scratch with an empty page'),
    };
  }

  if (isAllBlocksPageSelected) {
    return {
      ...defaultCaptions,
      button: 'Create test page',
      pageTemplateSubtitle: 'Create a test page with all blocks',
    };
  }

  if (openWebsiteSettings) {
    return {
      ...defaultCaptions,
      pageTemplateSubtitle: (
        <>
          {i18next.t(
            'Have AI create this page from your business profile. Customize it under {link}',
            {
              link: '',
            }
          )}{' '}
          <a onClick={openWebsiteSettings} href="/">
            {i18next.t('Settings')} &#x2192; {i18next.t('Business Info')}
          </a>
        </>
      ),
    };
  }

  return defaultCaptions;
};

function AddPageContainer({
  pageType = pageTypes.EMPTY_PAGE,
  pageName = null,
  onAddPageFromTemplate,
  onDialogMainAction,
  onDialogCancel,
}) {
  const [featureFlags] = useFeatureFlags(['business_info_in_website_settings', 'site_export']);
  const {
    business_info_in_website_settings: isBusinessInfoInWebsiteSettingsEnabled,
    site_export: isSiteExportEnabled,
  } = featureFlags;
  const siteSettings = useSelector(site.selectors.getSettings);
  const { businessDescription } = siteSettings;
  const isTestPageEnabled = useSelector(config.selectors.getTestPagesEnabled);

  const pageOptions = useMemo(() => {
    const optionsToCreatePage = getOptionsToCreatePage();
    if (isTestPageEnabled || isSiteExportEnabled) {
      optionsToCreatePage.push({
        name: 'All blocks',
        id: pageTypes.ALL_BLOCKS,
      });
    }
    return optionsToCreatePage.map((option) => ({
      label: option.name,
      value: option.id,
    }));
  }, [isTestPageEnabled, isSiteExportEnabled]);

  const [selectedOption, setSelectedOption] = useState(
    () => pageOptions.find((option) => option.value === pageType) || pageOptions[0]
  );

  const dispatch = useDispatch();

  const locale = useSelector(siteLocales.selectors.getCurrentLocale);
  const pages = useSelector(site.accessors.getPages);
  const sameLocalePages = pages.filter((page) => page.locale === locale);
  const uniquePageName =
    pageName || generateUniquePageName(sameLocalePages, getPageNameByType(pageType));

  const { control, handleSubmit, formState, getValues, setValue, clearErrors, watch } = useForm({
    defaultValues: {
      [fieldNames.PAGE_NAME_INPUT]: uniquePageName,
      [fieldNames.DISPLAY_IN_NAVIGATION_CHECKBOX]: true,
      [fieldNames.PAGE_TEMPLATE_SELECT]: selectedOption.value,
      [fieldNames.USER_INPUT]: '',
    },
    mode: 'onTouched',
  });
  const { onNextDialog } = useAiOnboarding();

  const selectedTemplateId = watch(fieldNames.PAGE_TEMPLATE_SELECT);
  const userInputCaptions = useMemo(
    () => getUserInputCaptions(selectedTemplateId),
    [selectedTemplateId]
  );

  const fields = useMemo(() => {
    const baseFields = {
      [fieldNames.PAGE_NAME_INPUT]: {
        rules: {
          required: i18next.t('Field is required'),
          validate: validatePageName,
        },
      },
    };

    if (!userInputCaptions) {
      return baseFields;
    }

    return {
      ...baseFields,
      [fieldNames.USER_INPUT]: {
        control,
        ...userInputCaptions,
        rules: {
          maxLength: {
            value: MAX_USER_INPUT_CHARS,
            message: i18next.t(`Maximum number of characters {number}`, {
              number: MAX_USER_INPUT_CHARS,
            }),
          },
        },
      },
    };
  }, [control, userInputCaptions]);

  const onPageOptionChange = (option) => {
    clearErrors([fieldNames.PAGE_NAME_INPUT, fieldNames.USER_INPUT]);
    setSelectedOption(option);
    setValue(
      fieldNames.PAGE_NAME_INPUT,
      generateUniquePageName(sameLocalePages, getPageNameByType(option.value))
    );

    if (!pageTemplatesWithBusinessDescription.includes(option.value)) {
      setValue(fieldNames.USER_INPUT, '');
    }
  };

  const onCancel = () => {
    onDialogCancel();
    dispatch(dialogs.actions.show(dialogTypes.PAGE_MANAGER));
  };

  const onSubmit = (data) => {
    const selectedPageType = data[fieldNames.PAGE_TEMPLATE_SELECT];

    const userInput = data[fieldNames.USER_INPUT]?.trim();

    onDialogMainAction({
      pageTemplateId:
        selectedPageType === pageTypes.EMPTY_PAGE ? constants.BLANK_PAGE_TEMPLATE_ID : null,
      displayInNavigation: data[fieldNames.DISPLAY_IN_NAVIGATION_CHECKBOX],
      recipeId:
        selectedPageType !== pageTypes.EMPTY_PAGE ? data[fieldNames.PAGE_TEMPLATE_SELECT] : null,
      generateWithAi: data[fieldNames.PAGE_TEMPLATE_SELECT] !== pageTypes.EMPTY_PAGE,
      pageName: data[fieldNames.PAGE_NAME_INPUT],
      ...(userInput && { userInput }),
    });

    if (selectedPageType === pageTypes.EMPTY_PAGE) {
      dialogs.operations.show(dialogTypes.SAVING_PROGRESS, {
        progressType: 'indeterminate',
        callback: () => {
          onAddPageFromTemplate({
            ...data,
            [fieldNames.PAGE_TEMPLATE_SELECT]: constants.BLANK_PAGE_TEMPLATE_ID,
          });
        },
      });

      return;
    }

    if (selectedPageType === pageTypes.ALL_BLOCKS) {
      dialogs.operations.show(dialogTypes.SAVING_PROGRESS, {
        progressType: 'indeterminate',
        callback: () => {
          onAddPageFromTemplate(data);
        },
      });

      return;
    }

    upsell.operations
      .requestFeatureAvailability(AI_PAGE_WIZARD)
      .then(() => {
        dialogs.operations.show(dialogTypes.SAVING_PROGRESS, {
          progressType: 'indeterminate',
          callback: () => {
            onNextDialog({
              sourceId: sourceIds.PAGE_WIZARD,
              title: i18next.t('Generate page'),
              onNext: () =>
                dialogs.operations.show(dialogTypes.PAGE_GENERATION_DIALOG, {
                  name: data[fieldNames.PAGE_NAME_INPUT],
                  displayInNavigation: data[fieldNames.DISPLAY_IN_NAVIGATION_CHECKBOX],
                  displayBusinessName: isBusinessInfoInWebsiteSettingsEnabled,
                  recipeId: selectedPageType,
                  locale,
                  ...(userInput && { userInput }),
                }),
            });
          },
        });
      })
      .catch(() => {
        // eslint-disable-next-line no-console
        console.log('Upgrade flow was canceled');
      });
  };

  const openWebsiteSettings = (e) => {
    e.preventDefault();

    dialogs.operations.show(dialogTypes.WEBSITE_SETTINGS, {
      activeTabId: tabIdentifiers.BUSINESS_INFO,
      onAfterDialogClose: () => {
        dialogs.operations.show(dialogTypes.ADD_PAGE_DIALOG, {
          pageType: selectedOption.value,
          pageName: getValues(fieldNames.PAGE_NAME_INPUT),
          onAddPageFromTemplate,
        });
      },
    });
  };

  const isEmptyPageSelected = selectedOption.value === pageTypes.EMPTY_PAGE;
  const isAllBlocksPageSelected = selectedOption.value === pageTypes.ALL_BLOCKS;
  const captions = getCaptions({
    isEmptyPageSelected,
    isAllBlocksPageSelected,
    ...(isBusinessInfoInWebsiteSettingsEnabled && { openWebsiteSettings }),
  });

  useEffect(() => {
    if (!pageTemplatesWithBusinessDescription.includes(selectedTemplateId)) {
      return;
    }
    setValue(fieldNames.USER_INPUT, businessDescription || '');
  }, [businessDescription, selectedTemplateId, setValue]);

  return (
    <AddPageDialog
      onCancel={onCancel}
      onSubmit={handleSubmit(onSubmit)}
      pageOptions={pageOptions}
      selectedOption={selectedOption}
      onPageOptionChange={onPageOptionChange}
      captions={captions}
      fields={fields}
      control={control}
      formErrors={formState.errors}
      showPreviewLabel={!isEmptyPageSelected && !isAllBlocksPageSelected}
    />
  );
}

AddPageContainer.propTypes = {
  pageType: PropTypes.string,
  pageName: PropTypes.string,
  onAddPageFromTemplate: PropTypes.func.isRequired,
  onDialogMainAction: PropTypes.func.isRequired,
  onDialogCancel: PropTypes.func.isRequired,
};

export default AddPageContainer;
